import { Component, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { AddressBookEntry, AddressBookEntryRequest, AddressBookPostalAddress } from './address-book.models';
import { AddressBookAddressFieldsComponent } from './address-book-address-fields.component';
import { addressBookErrorMessage } from './address-book-name-dialog.component';
import { AddressBookService } from './address-book.service';

export interface AddressBookEntryDialogData {
  addressBookId: number;
  entry?: AddressBookEntry;
}

@Component({
  selector: 'app-address-book-entry-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule,
    MatInputModule, MatProgressSpinnerModule, MatRadioModule, AddressBookAddressFieldsComponent],
  templateUrl: './address-book-entry-dialog.component.html',
  styleUrls: ['./address-book-entry-dialog.component.scss']
})
export class AddressBookEntryDialogComponent {
  form: FormGroup;
  submitting = false;
  errorMessage = '';

  constructor(
    private readonly fb: FormBuilder,
    readonly dialogRef: MatDialogRef<AddressBookEntryDialogComponent, AddressBookEntry>,
    @Inject(MAT_DIALOG_DATA) readonly data: AddressBookEntryDialogData,
    private readonly addressBookService: AddressBookService
  ) {
    this.form = this.fb.group({
      type: [data.entry?.type ?? 'PERSON', Validators.required],
      organisationName: [data.entry?.organisationName ?? ''],
      title: [data.entry?.title ?? ''],
      firstName: [data.entry?.firstName ?? ''],
      lastName: [data.entry?.lastName ?? ''],
      businessAddress: this.addressGroup(data.entry?.businessAddress),
      legalAddress: this.addressGroup(data.entry?.legalAddress),
      personAddress: this.addressGroup(data.entry?.personAddress),
      dids: [toLines(data.entry?.dids)],
      websites: [toLines(data.entry?.websites), websitesValidator],
      emails: [toLines(data.entry?.emails), emailsValidator],
      phoneNumbers: [toPhoneLines(data.entry?.phoneNumbers), phoneNumbersValidator],
      organisations: [toLines(data.entry?.organisations)]
    });
    this.configureTypeValidators(this.form.controls['type'].value);
    this.form.controls['type'].valueChanges.subscribe(type => this.configureTypeValidators(type));
  }

  get isPerson(): boolean {
    return this.form.controls['type'].value === 'PERSON';
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.submitting) return;
    this.submitting = true;
    this.errorMessage = '';
    const value = this.form.getRawValue();
    const request: AddressBookEntryRequest = {
      type: value.type,
      organisationName: this.isPerson ? undefined : clean(value.organisationName),
      title: this.isPerson ? clean(value.title) : undefined,
      firstName: this.isPerson ? clean(value.firstName) : undefined,
      lastName: this.isPerson ? clean(value.lastName) : undefined,
      businessAddress: this.isPerson ? undefined : compactAddress(value.businessAddress),
      legalAddress: this.isPerson ? undefined : compactAddress(value.legalAddress),
      personAddress: this.isPerson ? compactAddress(value.personAddress) : undefined,
      dids: parseLines(value.dids),
      websites: parseLines(value.websites),
      emails: parseLines(value.emails),
      phoneNumbers: parsePhoneLines(value.phoneNumbers),
      organisations: this.isPerson ? parseLines(value.organisations) : []
    };
    const call = this.data.entry
      ? this.addressBookService.updateEntry(this.data.addressBookId, this.data.entry.addressBookEntryId, request)
      : this.addressBookService.createEntry(this.data.addressBookId, request);
    call.subscribe({
      next: entry => this.dialogRef.close(entry),
      error: error => {
        this.submitting = false;
        this.errorMessage = addressBookErrorMessage(error, 'Der Adressbucheintrag konnte nicht gespeichert werden.');
      }
    });
  }

  private addressGroup(address?: AddressBookPostalAddress): FormGroup {
    return this.fb.group({
      street: [address?.street ?? ''],
      houseNumber: [address?.houseNumber ?? ''],
      postalCode: [address?.postalCode ?? ''],
      city: [address?.city ?? ''],
      state: [address?.state ?? ''],
      countryCode: [address?.countryCode ?? browserCountryCode(), Validators.pattern(/^[A-Za-z]{2}$/)]
    }, { validators: addressValidator });
  }

  private configureTypeValidators(type: string): void {
    const organisationName = this.form.controls['organisationName'];
    const firstName = this.form.controls['firstName'];
    const lastName = this.form.controls['lastName'];
    organisationName.setValidators(type === 'ORGANISATION' ? [Validators.required, Validators.maxLength(255)] : []);
    firstName.setValidators(type === 'PERSON' ? [Validators.maxLength(255)] : []);
    lastName.setValidators(type === 'PERSON' ? [Validators.required, Validators.maxLength(255)] : []);
    organisationName.updateValueAndValidity({ emitEvent: false });
    firstName.updateValueAndValidity({ emitEvent: false });
    lastName.updateValueAndValidity({ emitEvent: false });
  }
}

function clean(value: unknown): string | undefined {
  const normalized = String(value ?? '').trim();
  return normalized || undefined;
}

function parseLines(value: unknown): string[] {
  return [...new Set(String(value ?? '').split(/\r?\n|,/).map(item => item.trim()).filter(Boolean))];
}

function compactAddress(value: any): AddressBookPostalAddress | undefined {
  const address: AddressBookPostalAddress = {
    street: clean(value?.street),
    houseNumber: clean(value?.houseNumber),
    postalCode: clean(value?.postalCode),
    city: clean(value?.city),
    state: clean(value?.state),
    countryCode: clean(value?.countryCode)?.toUpperCase()
  };
  return [address.street, address.houseNumber, address.postalCode, address.city, address.state].some(Boolean)
    ? address
    : undefined;
}

function addressValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value ?? {};
  const hasAddress = [value.street, value.houseNumber, value.postalCode, value.city, value.state]
    .some(item => Boolean(clean(item)));
  if (!hasAddress) return null;
  return [value.street, value.houseNumber, value.postalCode, value.city].every(item => Boolean(clean(item)))
    ? null
    : { incompleteAddress: true };
}

function browserCountryCode(): string {
  if (typeof navigator === 'undefined') return '';
  const parts = (navigator.language ?? '').split(/[-_]/);
  const region = parts.length > 1 ? parts[parts.length - 1] : '';
  return /^[A-Za-z]{2}$/.test(region) ? region.toUpperCase() : '';
}

function parsePhoneLines(value: unknown): { countryCode: string; number: string }[] {
  return String(value ?? '').split(/\r?\n/).map(line => line.trim()).filter(Boolean).map(line => {
    const separator = line.indexOf('|');
    return {
      countryCode: line.slice(0, separator).trim(),
      number: line.slice(separator + 1).trim()
    };
  });
}

function toLines(values?: string[]): string {
  return values?.join('\n') ?? '';
}

function toPhoneLines(values?: { countryCode: string; number: string }[]): string {
  return values?.map(phone => `${phone.countryCode} | ${phone.number}`).join('\n') ?? '';
}

function emailsValidator(control: AbstractControl): ValidationErrors | null {
  const invalid = parseLines(control.value).some(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  return invalid ? { emails: true } : null;
}

function websitesValidator(control: AbstractControl): ValidationErrors | null {
  const invalid = parseLines(control.value).some(value => {
    try {
      const url = new URL(value);
      return url.protocol !== 'http:' && url.protocol !== 'https:';
    } catch {
      return true;
    }
  });
  return invalid ? { websites: true } : null;
}

function phoneNumbersValidator(control: AbstractControl): ValidationErrors | null {
  const invalid = String(control.value ?? '').split(/\r?\n/).map(line => line.trim()).filter(Boolean)
    .some(line => /^\+[1-9][0-9]{0,3}\s*\|\s*[0-9() /.+-]{3,30}$/.test(line) === false);
  return invalid ? { phoneNumbers: true } : null;
}
